/**
 * Parse a duration string into its numeric value and unit
 * @param {string} str - Duration string (e.g., "30D" or "6M")
 * @returns {object|null} Object with value and unit, or null if invalid format
 */
export const parseDuration = (str) => {
  const match = str.match(/^(\d+)([A-Za-z]+)$/);
  return match ? { value: parseInt(match[1], 10), unit: match[2] } : null;
};

/**
 * Convert durations to standardized day values
 * @param {Array} durations - Array of duration strings
 * @returns {Array} Array of numeric day values
 */
export const parseCustomDuration = (durations = []) =>
  durations.map(d => {
    const parsed = parseDuration(d);
    if (!parsed) return 0;
    if(parsed.unit === 'D') return parsed.value;
    if(parsed.unit === 'M') return parsed.value * 30;
  });

/**
 * Get available duration range based on selected amount and product rules
 * @param {object} params - Configuration parameters
 * @returns {Array} Array of valid duration strings
 */
export const getDurationRange = (params) => {
  const {
    durationRange,
    method,
    hasCombination,
    combination,
    upperConstant,
    lowerConstant,
    amountSelected
  } = params;

  const parsedDurations = parseCustomDuration(durationRange);

  if (method === 'dynamic' && hasCombination) {
    const min = Math.min(...parsedDurations);
    const max = Math.max(...parsedDurations);
    const allowedMin = Math.max(min / 30, Math.ceil(amountSelected / upperConstant));
    const allowedMax = Math.min(max / 30, Math.floor(amountSelected / lowerConstant));

    return parsedDurations
      .filter(d => d >= allowedMin * 30 && d <= allowedMax * 30)
      .map(d => d >= 30 ? `${d}D` : `${d*30}D`);
  }

  if (method === 'fixed' && hasCombination) {
    for (const key in combination) {
      const [min, max] = key.split("_").map(Number);

      if (amountSelected >= min && amountSelected <= max) {
        const { min_duration, max_duration } = combination[key];
        const minVal = parseDuration(min_duration).value;
        const unit = parseDuration(min_duration).unit;
        const maxVal = parseDuration(max_duration).value;
        const parsedMin = unit === 'M' ? minVal * 30 : minVal;
        const parsedMax = unit === 'M' ? maxVal * 30 : maxVal;
        const minIdx = parsedDurations.indexOf(parsedMin);
        const maxIdx = parsedDurations.indexOf(parsedMax);

        return parsedDurations
          .slice(minIdx, maxIdx + 1)
          .map(d => `${d}D`);
      }
    }
  }

  return durationRange;
};

/**
 * Convert duration values to months for display
 * @param {Array} durationStrings - Array of duration strings
 * @returns {Array} Array of month values
 */
export const convertDurationsToMonths = (durationStrings = []) => {
  return durationStrings.map(d => {
    const { value, unit } = parseDuration(d);
    return unit === "D" ? value / 30 : value;
  });
};

/**
 * Find a formatted duration string that matches a month value
 * @param {Array} durationRange - Available duration strings
 * @param {number} monthValue - Selected duration in months
 * @returns {string|null} Matching duration string or null
 */
export const findDurationFormat = (durationRange, monthValue) => {
  const targetDays = monthValue * 30;
  const monthStr = `${monthValue}M`;
  const daysStr = `${targetDays}D`;
  if (durationRange.includes(monthStr)) {
    return monthStr;
  }
  if (durationRange.includes(daysStr)) {
    return daysStr;
  }
};
